// Teacher Workload Tab - SAT V4 Zone
import { DsCard, DsTypography } from '@ice-web-app/designSystem';
import {
  Box,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import { WeekToggle } from '../../../../lib/components';
import type { WeekType } from '../../../../lib/components';
import { mockClasses, mockSubjects } from '../../../../lib/mockData';
import {
  getPeriodsColumnHeader,
  getSubjectPeriodsValue,
  getTeacherCurrentPeriods,
  getTeacherMaxPeriods,
} from '../../../../lib/utils';
import { useSettingsStore } from '../../../../stores';
import type { DayOfWeek, Teacher } from '../../../../types';

interface WorkloadTabProps {
  teacher: Teacher;
}

const DAYS: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

const WorkloadTab: FC<WorkloadTabProps> = ({ teacher }) => {
  const { scheduleType } = useSettingsStore();
  const [selectedWeek, setSelectedWeek] = useState<WeekType>('A');

  const teacherClasses = mockClasses.filter((c) => c.teacherId === teacher.id);
  const totalPeriodsAssigned = getTeacherCurrentPeriods(
    teacher,
    scheduleType,
    scheduleType === 'Bi-Weekly' ? selectedWeek : undefined,
  );
  const maxPeriods = getTeacherMaxPeriods(
    teacher,
    scheduleType,
    scheduleType === 'Bi-Weekly' ? selectedWeek : undefined,
  );
  const utilizationPercent = Math.round((totalPeriodsAssigned / maxPeriods) * 100);

  // Mock distribution by day (would come from actual timetable entries in real app)
  const mockDayDistribution = DAYS.map((day, index) => ({
    day,
    periods: Math.floor(totalPeriodsAssigned / 5) + (index < totalPeriodsAssigned % 5 ? 1 : 0),
  }));

  const getUtilizationColor = () => {
    if (utilizationPercent > 100) return 'error';
    if (utilizationPercent > 85) return 'warning';
    return 'success';
  };

  return (
    <Stack spacing={3}>
      {/* Week Toggle for Bi-Weekly */}
      {scheduleType === 'Bi-Weekly' && <WeekToggle selectedWeek={selectedWeek} onChange={setSelectedWeek} />}

      {/* Workload Summary */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <DsCard sx={{ p: 3 }}>
            <DsTypography variant="h6" fontWeight={600} gutterBottom>
              Period Allocation
            </DsTypography>
            <Stack spacing={2}>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <DsTypography variant="body2">
                    {totalPeriodsAssigned} of {maxPeriods} periods assigned
                  </DsTypography>
                  <DsTypography variant="body2" fontWeight={600}>
                    {utilizationPercent}%
                  </DsTypography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(utilizationPercent, 100)}
                  color={getUtilizationColor()}
                  sx={{ height: 10, borderRadius: 1 }}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <DsTypography variant="h5" fontWeight={700} color="primary.main">
                      {totalPeriodsAssigned}
                    </DsTypography>
                    <DsTypography variant="caption" color="text.secondary">
                      Assigned
                    </DsTypography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <DsTypography variant="h5" fontWeight={700} color="success.main">
                      {Math.max(0, maxPeriods - totalPeriodsAssigned)}
                    </DsTypography>
                    <DsTypography variant="caption" color="text.secondary">
                      Available
                    </DsTypography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <DsTypography variant="h5" fontWeight={700} color="info.main">
                      {maxPeriods}
                    </DsTypography>
                    <DsTypography variant="caption" color="text.secondary">
                      Maximum
                    </DsTypography>
                  </Box>
                </Grid>
              </Grid>
            </Stack>
          </DsCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <DsCard sx={{ p: 3, height: '100%' }}>
            <DsTypography variant="h6" fontWeight={600} gutterBottom>
              Weekly Distribution
            </DsTypography>
            <Stack spacing={1}>
              {mockDayDistribution.map((item) => (
                <Stack key={item.day} direction="row" alignItems="center" spacing={2}>
                  <DsTypography variant="body2" sx={{ width: 80, textTransform: 'capitalize' }}>
                    {item.day}
                  </DsTypography>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(item.periods / 8) * 100}
                      sx={{ height: 16, borderRadius: 1 }}
                    />
                  </Box>
                  <DsTypography variant="body2" fontWeight={600} sx={{ width: 30, textAlign: 'right' }}>
                    {item.periods}
                  </DsTypography>
                </Stack>
              ))}
            </Stack>
          </DsCard>
        </Grid>
      </Grid>

      {/* Status Indicator */}
      {utilizationPercent > 100 && (
        <DsCard sx={{ p: 2, bgcolor: 'error.light' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip label="Overloaded" color="error" size="small" />
            <DsTypography variant="body2">
              This teacher is assigned {totalPeriodsAssigned - maxPeriods} periods over their maximum capacity.
            </DsTypography>
          </Stack>
        </DsCard>
      )}

      {/* Classes Breakdown */}
      <DsCard sx={{ p: 3 }}>
        <DsTypography variant="h6" fontWeight={600} gutterBottom>
          Teaching Load by Class
        </DsTypography>
        {teacherClasses.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Year Group</TableCell>
                  <TableCell>Class Name</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell align="center">Sets/Bands</TableCell>
                  <TableCell align="center">Pupils</TableCell>
                  <TableCell align="center">{getPeriodsColumnHeader(scheduleType)}</TableCell>
                  <TableCell align="center">% of Workload</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teacherClasses.map((cls) => {
                  const subject = mockSubjects.find((s) => s.id === cls.subjectId);
                  const periodsForClass = subject
                    ? getSubjectPeriodsValue(
                        subject,
                        cls.yearGroup,
                        scheduleType,
                        scheduleType === 'Bi-Weekly' ? selectedWeek : undefined,
                      )
                    : 0;
                  const workloadPercent =
                    totalPeriodsAssigned > 0 ? Math.round((periodsForClass / totalPeriodsAssigned) * 100) : 0;

                  // Year group colors
                  const yearColors: Record<number, string> = {
                    7: '#4CAF50',
                    8: '#2196F3',
                    9: '#9C27B0',
                    10: '#FF9800',
                    11: '#F44336',
                  };
                  const yearColor = yearColors[cls.yearGroup] || '#9E9E9E';

                  return (
                    <TableRow key={cls.id}>
                      <TableCell align="center">
                        <Chip
                          label={`Year ${cls.yearGroup}`}
                          size="small"
                          sx={{
                            bgcolor: `${yearColor}20`,
                            color: yearColor,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <DsTypography variant="body2" fontWeight={500}>
                          {cls.name}
                        </DsTypography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: subject?.color || '#9E9E9E',
                            }}
                          />
                          <span>{subject?.name || 'Unknown'}</span>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <DsTypography variant="body2" color="text.secondary">
                          {/* Alternate between Sets and Bands based on class */}
                          {cls.id.includes('eng') || cls.id.includes('mat')
                            ? `Set ${(Number.parseInt(cls.id.match(/\d+/)?.[0] || '1') % 5) + 1}`
                            : ['Band X', 'Band Y', 'Band Lower', 'Band Higher'][
                                Number.parseInt(cls.id.match(/\d+/)?.[0] || '1') % 4
                              ]}
                        </DsTypography>
                      </TableCell>
                      <TableCell align="center">{cls.pupilIds.length}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={periodsForClass}
                          size="small"
                          sx={{
                            bgcolor: 'primary.main',
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={`${workloadPercent}%`} size="small" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default', borderRadius: 2 }}>
            <DsTypography color="text.secondary">No classes assigned yet.</DsTypography>
          </Box>
        )}
      </DsCard>
    </Stack>
  );
};

export default WorkloadTab;
